import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { type DataChipProps, InfoAction, Sheet } from '@pkg/components';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { type ExpireOption, useTimeAgo } from '@pkg/hooks';
import { formatUsd } from '@pkg/utils';

import { useExchangeRatesStore } from '@app/store/zustand';

import { AssetCard, getNumberDirection, getNumberSign } from '../AssetCard';
import { ExpireSelector, type ExpireSelectorProps } from '../ExpireSelector';

import { useAssetOfferState } from './useAssetOfferState';

import { MainInterior, Section, SectionHeader, TitleMedium } from './AssetOfferSheet.style';

// TODO: Determine API for this "feature component".
export interface AssetOfferSheetProps {
    expiration?: ExpireOption;
    onClose: () => void;
    onExpirationChange: ExpireSelectorProps['onSelect'];
}

export function AssetOfferSheet({ expiration = 'week', onClose, onExpirationChange }: AssetOfferSheetProps) {
    const [translate] = useTranslation('marketplace');

    const {
        offerToken,
        floorUsdValue,
        floorUsd24hChange,
        floorPercentChange,
        lastSaleUsd,
        lastSaleUtcMs,
        localCurrency,
        localAppraisal,
        localPrice,
        nativePrice,
        formatAndUpdateLocalPrice,
        handlePriceBlur,
        handlePriceChange,
        handleLocalCurrencyChange,
        handleSubmit,
    } = useAssetOfferState();

    const convertCurrency = useExchangeRatesStore((state) => state.convertCurrency);
    const [statsList, setStatsList] = useState<DataChipProps[]>([]);

    async function handleSubmitOffer() {
        await handleSubmit();
        // Do we need to call `handleReset()`?
        onClose();
    }

    const { label: lastSaleTimeAgo } = useTimeAgo(lastSaleUtcMs, { preferDaysOverWeeks: true });
    const lastSaleLabel = lastSaleUtcMs ? `${formatUsd(lastSaleUsd, true)} | ${lastSaleTimeAgo.shorthand}` : undefined;

    useEffect(() => {
        function setPriceFromStat(usdValue = 0) {
            const converted = convertCurrency('usd', localCurrency, usdValue, true);
            formatAndUpdateLocalPrice(converted, localCurrency);
        }

        // Do we want to pass `disabled` and `pressed` props to each Stat?
        const updatedStatsList: DataChipProps[] = [];

        const floorPriceSuffixData = [
            getNumberSign(floorUsd24hChange),
            formatUsd(Math.abs(floorUsd24hChange), true),
            ` (${floorPercentChange}%)`,
        ];

        updatedStatsList.push({
            label: translate('common-chip-price'),
            value: formatUsd(floorUsdValue, true),
            suffix: floorPriceSuffixData.join(''),
            suffixType: getNumberDirection(floorUsd24hChange),
            ariaLabel: translate('common-chip-price-a11y'),
            onAction: () => setPriceFromStat(floorUsdValue),
        });

        if (lastSaleLabel) {
            updatedStatsList.push({
                label: translate('common-chip-last'),
                value: lastSaleLabel,
                ariaLabel: translate('common-chip-last-a11y'),
                onAction: () => setPriceFromStat(lastSaleUsd),
            });
        }

        setStatsList(updatedStatsList);
    }, [
        translate,
        localCurrency,
        floorUsdValue,
        floorUsd24hChange,
        floorPercentChange,
        lastSaleLabel,
        lastSaleUsd,
        formatAndUpdateLocalPrice,
        convertCurrency,
    ]);

    return (
        <Sheet gapless backdrop onClose={onClose}>
            <Sheet.Header largeTitle title={translate('asset-offer-sheet-title')} onClose={onClose} />

            <Sheet.Main>
                <Sheet.Layout>
                    <Sheet.Layout.Columns stretch>
                        <Sheet.Layout.Columns.Primary padded scrollable>
                            <MainInterior>
                                <Section>
                                    {offerToken ? (
                                        <AssetCard
                                            asset={offerToken}
                                            localCurrency={localCurrency}
                                            localPrice={localPrice}
                                            nativePrice={nativePrice}
                                            appraisal={localAppraisal}
                                            // error={}
                                            onPriceBlur={handlePriceBlur}
                                            onPriceChange={handlePriceChange}
                                            onLocalCurrencyChange={handleLocalCurrencyChange}
                                        >
                                            <AssetCard.StatsList items={statsList} />
                                        </AssetCard>
                                    ) : null}
                                </Section>

                                <Section>
                                    <SectionHeader>
                                        <TitleMedium>{translate('asset-offer-sheet-section-title-period')}</TitleMedium>

                                        <InfoAction
                                            message="TODO: Also requires tooltip!"
                                            aria-label={translate('common-info-action-a11y')}
                                        />
                                    </SectionHeader>

                                    <ExpireSelector selected={expiration} onSelect={onExpirationChange} />
                                </Section>
                            </MainInterior>
                        </Sheet.Layout.Columns.Primary>
                    </Sheet.Layout.Columns>

                    <Sheet.Layout.Footer>
                        <Button
                            fluid
                            disabled={!nativePrice}
                            size="large"
                            variant="solid"
                            aria-label={translate('asset-offer-sheet-cta-a11y')}
                            onClick={handleSubmitOffer}
                        >
                            {translate('asset-offer-sheet-cta')}
                        </Button>
                    </Sheet.Layout.Footer>
                </Sheet.Layout>
            </Sheet.Main>
        </Sheet>
    );
}
